<script setup lang="ts">
import type {
  ServiceCondition,
  LikeServiceCondition,
  NumericServiceCondition,
  LikeMatch,
  ComparisonOperator,
} from "@/utils/archival";
import { computed, ref } from "vue";

const props = defineProps<{
  condition: ServiceCondition;
}>();

const emit = defineEmits<{
  (e: "update", updated: ServiceCondition): void;
  (e: "remove"): void;
}>();

const LIKE_MATCHES: LikeMatch[] = ["up", "down", "any", "none"];
const OPERATORS: ComparisonOperator[] = ["=", "≠", "<", "≤", "≥", ">"];
const DEFAULT_STARS_MAX = 5;

const isLike = computed(() => props.condition.serviceType === "like");
const isStars = computed(() => props.condition.serviceType === "stars");

const like = computed(() => props.condition as LikeServiceCondition);
const numeric = computed(() => props.condition as NumericServiceCondition);

const editingOp = ref(false);
const editingVal = ref(false);

function toggleNegated() {
  emit("update", { ...props.condition, negated: !props.condition.negated });
}

function setLikeMatch(match: LikeMatch) {
  emit("update", { ...like.value, match });
  editingOp.value = false;
}

function setOperator(op: ComparisonOperator) {
  emit("update", { ...numeric.value, operator: op });
  editingOp.value = false;
}

function setStarsValue(val: number) {
  emit("update", { ...numeric.value, value: val });
  editingVal.value = false;
}

function handleCounterChange(e: Event) {
  const val = +(e.target as HTMLInputElement).value;
  if (!Number.isNaN(val) && val >= 0) {
    emit("update", { ...numeric.value, value: val });
  }
  editingVal.value = false;
}

function starRow(n: number): string {
  return Array.from({ length: DEFAULT_STARS_MAX }, (_, i) => (i < n ? "★" : "☆")).join("");
}

const starsLabel = computed(() => starRow(numeric.value.value ?? 0));
</script>

<template>
  <span class="service-chip" :class="{ 'service-chip--negated': condition.negated }">
    <button
      class="chip-not"
      :class="{ active: condition.negated }"
      :aria-pressed="condition.negated"
      title="Toggle NOT"
      @click.stop="toggleNegated"
    >¬</button>

    <span class="chip-name">{{ condition.serviceName }}</span>
    <span class="chip-sep">|</span>

    <!-- Like service: match picker -->
    <span v-if="isLike" class="chip-op chip-clickable" @click.stop="editingOp = !editingOp">
      {{ like.match }}
      <div v-if="editingOp" class="chip-popup" @click.stop>
        <button
          v-for="m in LIKE_MATCHES"
          :key="m"
          :class="{ selected: m === like.match }"
          @click.stop="setLikeMatch(m)"
        >{{ m }}</button>
      </div>
    </span>

    <!-- Numeric (stars/counter): operator + value -->
    <template v-else>
      <span class="chip-op chip-clickable" @click.stop="editingOp = !editingOp">
        {{ numeric.operator }}
        <div v-if="editingOp" class="chip-popup" @click.stop>
          <button
            v-for="op in OPERATORS"
            :key="op"
            :class="{ selected: op === numeric.operator }"
            @click.stop="setOperator(op)"
          >{{ op }}</button>
        </div>
      </span>
      <span class="chip-sep">|</span>

      <!-- Stars: star chooser -->
      <span v-if="isStars" class="chip-val chip-clickable" @click.stop="editingVal = !editingVal">
        {{ starsLabel }}
        <div v-if="editingVal" class="chip-popup" @click.stop>
          <button
            v-for="n in DEFAULT_STARS_MAX + 1"
            :key="n - 1"
            :class="{ selected: numeric.value === n - 1 }"
            @click.stop="setStarsValue(n - 1)"
          >{{ starRow(n - 1) }}</button>
        </div>
      </span>

      <!-- Counter: number input -->
      <span v-else class="chip-val chip-clickable" @click.stop="editingVal = !editingVal">
        <template v-if="!editingVal">{{ numeric.value }}</template>
        <input
          v-else
          type="number"
          min="0"
          :value="numeric.value"
          class="chip-number-input"
          @change="handleCounterChange"
          @blur="editingVal = false"
          @keydown.enter.stop="handleCounterChange"
          @keydown.escape.stop="editingVal = false"
          @click.stop
        />
      </span>
    </template>

    <span class="chip-sep">|</span>
    <button class="chip-remove" title="Remove" @click.stop="emit('remove')">✕</button>
  </span>
</template>

<style scoped lang="scss">
.service-chip {
  display: inline-flex;
  align-items: center;
  gap: 0;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  font-size: 0.82em;
  overflow: visible;
  position: relative;

  &.service-chip--negated {
    opacity: 0.75;
    border-color: var(--accent);
    background: color-mix(in srgb, var(--accent) 8%, var(--bg-elevated));
  }

  > * {
    padding: 2px 5px;
  }
}

.chip-sep {
  color: var(--text-muted, var(--border));
  padding: 2px 2px;
  user-select: none;
}

.chip-not {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted, #888);
  font-weight: bold;
  padding: 2px 4px;
  border-radius: var(--radius-sm, 2px);

  &.active {
    color: var(--accent);
    background: color-mix(in srgb, var(--accent) 15%, transparent);
  }

  &:hover {
    background: color-mix(in srgb, var(--accent) 10%, transparent);
  }
}

.chip-name {
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
}

.chip-op, .chip-val {
  color: var(--text-secondary, var(--text-primary));
  white-space: nowrap;
}

.chip-clickable {
  cursor: pointer;
  position: relative;
  border-radius: var(--radius-sm, 2px);

  &:hover {
    background: color-mix(in srgb, var(--accent) 12%, transparent);
    color: var(--accent);
  }
}

.chip-remove {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted, #888);
  padding: 2px 5px;
  border-radius: 0 var(--radius-md) var(--radius-md) 0;

  &:hover {
    color: var(--danger, #e55);
    background: color-mix(in srgb, var(--danger, #e55) 12%, transparent);
  }
}

.chip-popup {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  z-index: 200;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  min-width: 80px;
  overflow: hidden;

  button {
    background: none;
    border: none;
    padding: 5px 10px;
    cursor: pointer;
    text-align: left;
    color: var(--text-primary);
    font-size: 0.9em;
    white-space: nowrap;

    &:hover, &.selected {
      background: color-mix(in srgb, var(--accent) 15%, var(--bg-elevated));
    }
    &.selected {
      font-weight: 600;
      color: var(--accent);
    }
  }
}

.chip-number-input {
  width: 60px;
  background: var(--bg-elevated);
  color: var(--text-primary);
  border: 1px solid var(--accent);
  border-radius: var(--radius-sm, 2px);
  padding: 1px 4px;
  font-size: 0.9em;
}
</style>
